import { Box, Chip, Grid, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../../../components/ui/Section';
import { SupplierEsgProfile } from '../../../../../mocks/data/supplierEsgDashboardData';

export interface SocialTabProps {
  profiles: SupplierEsgProfile[];
}

const BoolChip = ({ value, trueLabel, falseLabel }: { value: boolean; trueLabel: string; falseLabel: string }) => (
  <Chip label={value ? trueLabel : falseLabel}
    size="small"
    color={value ? 'success' : 'error'}
    variant="outlined" />
);

const SocialTab: React.FC<SocialTabProps> = ({ profiles }) => {
  const { t } = useTranslation('esgDashboard');
  const modernSlaveryNonCompliant = profiles.filter((p) => !p.modernSlaveryCompliant).length;
  const smeCount = profiles.filter((p) => p.isSme).length;
  const diverseOwnedCount = profiles.filter((p) => p.isDiverseOwned).length;

  return (
    <Grid container
      spacing={4}>
      <Grid item
        xs={12}>
        <Stack direction="row"
          spacing={4}>
          <Typography variant="textSM">{t('social.nonCompliantStat', 'Modern slavery non-compliant:')} <b>{modernSlaveryNonCompliant}</b></Typography>
          <Typography variant="textSM">{t('social.smeStat', 'SME suppliers:')} <b>{smeCount}</b></Typography>
          <Typography variant="textSM">{t('social.diverseOwnedStat', 'Diverse-owned suppliers:')} <b>{diverseOwnedCount}</b></Typography>
        </Stack>
      </Grid>
      <Grid item
        xs={12}>
        <Section style={{ boxShadow: 'none' }}>
          <Box sx={{ p: 4 }}>
            <Typography variant="textSM"
              fontWeight={600}
              sx={{ mb: 2 }}>{t('social.profileTableTitle', 'Social profile by supplier')}</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('common.supplier', 'Supplier')}</TableCell>
                  <TableCell align="center">{t('social.modernSlaveryCompliant', 'Modern slavery compliant')}</TableCell>
                  <TableCell align="center">{t('social.ethicalSourcingDeclared', 'Ethical sourcing declared')}</TableCell>
                  <TableCell align="right">{t('social.labourStandards', 'Labour standards')}</TableCell>
                  <TableCell align="center">{t('social.sme', 'SME')}</TableCell>
                  <TableCell align="center">{t('social.diverseOwned', 'Diverse-owned')}</TableCell>
                  <TableCell align="right">{t('social.communityProgrammes', 'Community programmes')}</TableCell>
                  <TableCell align="right">{t('social.employeeWellbeing', 'Employee wellbeing')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {profiles.map((p) => (
                  <TableRow key={p.supplierId}>
                    <TableCell>{p.name}</TableCell>
                    <TableCell align="center"><BoolChip value={p.modernSlaveryCompliant}
                      trueLabel={t('common.compliant', 'Compliant')}
                      falseLabel={t('common.nonCompliant', 'Non-compliant')} /></TableCell>
                    <TableCell align="center"><BoolChip value={p.ethicalSourcingDeclared}
                      trueLabel={t('common.declared', 'Declared')}
                      falseLabel={t('common.notDeclared', 'Not declared')} /></TableCell>
                    <TableCell align="right">{p.labourStandardsScore}</TableCell>
                    <TableCell align="center">{p.isSme ? t('common.yes', 'Yes') : t('common.no', 'No')}</TableCell>
                    <TableCell align="center">{p.isDiverseOwned ? t('common.yes', 'Yes') : t('common.no', 'No')}</TableCell>
                    <TableCell align="right">{p.communityInvestmentProgrammes}</TableCell>
                    <TableCell align="right">{p.employeeWellbeingScore}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Section>
      </Grid>
    </Grid>
  );
};

export default SocialTab;
